/**
 * Issue #86 — Zod wire schemas for the `chores` / `status_changed` WS verb.
 *
 * Single source of truth for the eight frames that verb can carry. Both
 * apps/api (construction) and apps/web (receipt) import from here, so the
 * field names cannot drift across the workspace boundary — the MED-8
 * schema-drift guard, same shape as ./shopping/messages.ts.
 *
 * Drift on this topic has killed a celebration tier twice already, both times
 * silently and both times for months:
 *   - issue #79: the approval frame shipped only the approver's id, so the L3
 *     burst (which gates on the EARNER) never fired.
 *   - issue #83: the claim frame shipped `by`, the client read `claimed_by`,
 *     and the whole L1 tier was dead.
 * Both suites stayed green throughout, because each side asserted against a
 * fixture it wrote itself. CHORE_STATUS_FRAMES at the bottom of this file is
 * the fixture both sides now assert against instead.
 *
 * Six design constraints, each load-bearing:
 *
 *  1. NOT `z.discriminatedUnion('status', …)`. `'available'` is the status on
 *     four different verbs (rejected / expired / reassigned / restored), so a
 *     status discriminant cannot separate the members. Plain `z.union`.
 *
 *     This costs less at compile time than it looks like it should. TS's
 *     excess-property check against a plain union normally admits any key
 *     present on ANY constituent — which would have let `points:` onto a
 *     claim frame and deferred the error to a runtime throw. It does not:
 *     TS narrows on the `status` literal first, so a cross-member key is
 *     rejected at the call site (verified by sabotage: adding `points` to the
 *     claim frame gives TS2353, not a clean compile). The four `available`
 *     members are the one place where that narrowing cannot separate them —
 *     since issue #85 dropped the reject frame's freetext they all carry a
 *     `cause` literal instead, which would now make a real
 *     `discriminatedUnion('cause', …)` possible for that subset; that is a
 *     restructuring of its own and is not built here on spec. `.strict()` at
 *     construction closes the gap either way.
 *
 *  2. No synthetic `verb` discriminant either. It would cost wire bytes
 *     against INV-WS-1 and change what ships beyond the rename this issue
 *     sanctioned.
 *
 *  3. Every field on every member is REQUIRED, never `.optional()`.
 *     `exactOptionalPropertyTypes: true` (tsconfig.base.json) means a
 *     misspelled OPTIONAL field is silently dropped rather than rejected —
 *     which is precisely the failure mode this module exists to stop.
 *
 *  4. Strict on construction, LOOSE on receipt. `ChoreStatusFrameOut` is
 *     `.strict()` and is what the server's `choreStatusFrame()` constructor
 *     parses through: an extra or misspelled key throws at the broadcast site,
 *     where it is cheap to notice. `ChoreStatusFrame` is the same union
 *     without `.strict()`, and is what the client `safeParse`s: the day the
 *     server adds a field, a strict client parse would make every
 *     Workbox-cached old PWA reject the entire frame and the tier would die
 *     again — the exact failure being fixed here. The asymmetry is the point.
 *
 *  5. Ids are `z.string().min(1)`, NOT a UUID regex. Existing fixtures use
 *     'i-1' / 'm-1' (see apps/web CelebrationProvider.test.tsx) and a regex
 *     would reject them for no benefit: the client only ever compares an id
 *     for equality, never parses it.
 *
 *  6. `hub.broadcast` no longer takes `data: unknown` on this verb. Issue #93
 *     landed the generic: see ../ws-frames.ts, which maps
 *     ('chores','status_changed') → ChoreStatusFrameOut and gives the hub
 *     `data: FrameFor<T, E>`. The constructor below is therefore no longer
 *     the only thing standing between a caller and a malformed frame — a
 *     broadcast on this verb that skips `choreStatusFrame()` is now a compile
 *     error at the call site, which is the hole the old wording described.
 *
 *     A TS *overload* narrowing ('chores','status_changed') remains a dead
 *     end and must not be tried again — re-verified while building #93. TS
 *     picks the first APPLICABLE signature, so a malformed payload falls
 *     straight through to the `unknown` one and compiles clean. It would look
 *     like a guard without being one. A generic parameter has no such escape:
 *     there is only one signature.
 *
 *     It does NOT type every call site at once — only the mapped pairs. The
 *     earlier claim here said "all 39"; that figure was wrong twice over, so
 *     this file no longer carries one. ../ws-frames.ts owns the map, the
 *     current counts, and the grep that produces them — read the number
 *     there, where registering a verb already updates it. Unmapped
 *     topic/event pairs resolve to `unknown` and compile exactly as they did
 *     before, which is what makes each entry additive, and also why #93 does
 *     not pre-empt the ⟶ v3.0 rename in issue #4 — `FrameFor` travels with
 *     the renamed signature unchanged.
 *
 *     TWO escape hatches survive, and they are why the strict parse below
 *     stays. (a) A cast — `as never`, a round-trip through `unknown`, a
 *     spread whose type was already widened. (b) A caller that takes the
 *     topic or event as a PARAMETER rather than a literal: `FrameFor` only
 *     resolves when both infer as literals, so `broadcast(topic, …)` inside
 *     such a helper falls back to `unknown` and the guard vanishes with no
 *     diagnostic. A union (`'chores' | 'feed'`) collapses the same way.
 *
 *     Note what does and does not catch those. Going through this
 *     constructor catches both, because `ChoreStatusFrameOut.parse()` runs on
 *     the payload. Skipping the constructor catches NEITHER — the hub's
 *     `sendTo` parses only the ServerMessage envelope, whose `data` is
 *     `z.unknown()`. So the pairing is: compile-time typing for the honest
 *     mistake, this constructor for the dishonest one, and nothing at all for
 *     a caller who evades both. That residue is the honest limit of #93.
 *     The type-level lock on the hub signature itself lives in
 *     apps/api/src/ws/hub.frames.assert.ts — a source file, not a test, for
 *     reasons that file explains.
 *
 * The status literals are DB enum values and must stay byte-identical: an
 * approval is `'completed'`, never `'approved'` (inventing the latter was
 * issue #79's mistake in the other direction).
 */
import { z } from 'zod';

/** Constraint 5: equality-compared ids, not parsed ones. */
const IdSchema = z.string().min(1);

// ── The eight members ───────────────────────────────────────────────────────
// `status` is a literal on every one (constraint 1 explains why that is still
// not enough to make them a discriminated union), and every field is required
// (constraint 3).

/** chore.claimed — the kid who won the claim race, plus the hold deadline. */
export const ChoreClaimedFrame = z
  .object({
    id: IdSchema,
    status: z.literal('claimed'),
    claimed_by: IdSchema,
    claimExpiresAt: z.iso.datetime(),
  })
  .strict();

/** chore.completed — submitted, now queued for a parent. */
export const ChoreAwaitingApprovalFrame = z
  .object({
    id: IdSchema,
    status: z.literal('awaiting_approval'),
    claimed_by: IdSchema,
  })
  .strict();

/**
 * chore.approved — the only frame carrying the L3 celebration fields, and the
 * only verb whose two actors are DIFFERENT PEOPLE: `approved_by` is the parent
 * who approved, `claimed_by` the kid who earned. The client gates its burst on
 * `claimed_by`, so an approving parent gets no confetti for someone else's
 * points. `points` + `prevBalance` drive the wallet count-up.
 *
 * The tight one against INV-WS-1: 202 bytes with real 36-char UUIDs, so about
 * 54 bytes of headroom left. Anything further belongs behind a refetch.
 */
export const ChoreApprovedFrame = z
  .object({
    id: IdSchema,
    status: z.literal('completed'),
    approved_by: IdSchema,
    claimed_by: IdSchema,
    points: z.number().int(),
    prevBalance: z.number().int(),
  })
  .strict();

/**
 * chore.rejected — back in the pool. Deliberately NO `reason`: the route caps
 * it at 140 *characters* while INV-WS-1 is a *byte* budget, and hub.broadcast
 * fans out to every screen in the house with no per-member filter, so one
 * kid's rejection note would reach all of them (issue #85, T-02-12). The kid
 * alone gets the full text, by Web Push.
 */
export const ChoreRejectedFrame = z
  .object({
    id: IdSchema,
    status: z.literal('available'),
    cause: z.literal('rejected'),
  })
  .strict();

/** chore.expired — the claim hold ran out. */
export const ChoreExpiredFrame = z
  .object({
    id: IdSchema,
    status: z.literal('available'),
    cause: z.literal('expired'),
  })
  .strict();

/** chore.reassigned — a parent moved the instance to someone else. */
export const ChoreReassignedFrame = z
  .object({
    id: IdSchema,
    status: z.literal('available'),
    cause: z.literal('reassigned'),
  })
  .strict();

/** chore.restored — a parent put a cancelled instance back in the pool. */
export const ChoreRestoredFrame = z
  .object({
    id: IdSchema,
    status: z.literal('available'),
    cause: z.literal('restored'),
  })
  .strict();

/**
 * chore.cancelled — its own terminal state, deliberately NOT reported as
 * 'expired': the kid-facing copy for expiry says the card went back in the
 * pool, which a cancel did not.
 */
export const ChoreCancelledFrame = z
  .object({
    id: IdSchema,
    status: z.literal('cancelled'),
    cause: z.literal('cancelled'),
  })
  .strict();

/**
 * Construction side (constraint 4): the strict members. An unknown or
 * misspelled key throws. Used by `choreStatusFrame()` below and by all three
 * test suites.
 */
export const ChoreStatusFrameOut = z.union([
  ChoreClaimedFrame,
  ChoreAwaitingApprovalFrame,
  ChoreApprovedFrame,
  ChoreRejectedFrame,
  ChoreExpiredFrame,
  ChoreReassignedFrame,
  ChoreRestoredFrame,
  ChoreCancelledFrame,
]);

/**
 * Receipt side (constraint 4): the same union, non-strict — unknown keys are
 * stripped instead of rejected, so a newer server that adds a field does not
 * make an old Workbox-cached client throw the whole frame away. This is what
 * apps/web `safeParse`s.
 *
 * Two fields are additionally OPTIONAL here and required above, and the
 * asymmetry is deliberate: `claimExpiresAt` and `approved_by` are the only
 * fields on THIS VERB that no client reads. The sole data-field consumer of
 * `status_changed` is the celebration hook, which reads `id`, `status`,
 * `claimed_by`, `points`, `prevBalance` and nothing else; `useChoresWS`
 * switches on the verb alone and never opens `data`. (Scope that claim to the
 * verb, not the topic: `useTabletSlaStrip.ts` reads `data.n` on
 * `sla_count_changed` and `StreakRiskMount.tsx` reads `data.memberId` on
 * `streak_risk_member`, both on the `chores` topic. Neither verb is described
 * by this module.) Requiring the two unread fields on receipt would turn any
 * future server-side rename of a field the client has no use for into a dead
 * tier — the very failure this module exists to stop, reintroduced through the
 * back door. `claimed_by` / `points` / `prevBalance` stay required, because a
 * frame missing those genuinely cannot drive a celebration and dropping it is
 * the correct outcome.
 *
 * #155: L3-skjermen leser nå også `approved_by`, for «Pappa godkjente …». Den
 * er fortsatt valgfri her med vilje: uten den faller teksten tilbake til en
 * setning uten navn, så en ramme som mangler den skal ikke kastes.
 *
 * This is also what makes a partial deploy survivable in the one direction
 * that would otherwise regress: an old api still sending `by` has its unknown
 * key stripped, and the approval frame then still parses, so L3 keeps firing
 * instead of going dark until the api container catches up.
 *
 * Note this costs nothing on the construction side. The compile-time lock
 * lives in `choreStatusFrame()`'s parameter type, which is inferred from
 * `ChoreStatusFrameOut` above — where every field is still required.
 *
 * `.strip()` before `.partial()` is NOT redundant and must not be tidied
 * away: `.partial()` inherits the strictness of the schema it is called on, so
 * `ChoreClaimedFrame.partial({claimExpiresAt:true})` alone would still REJECT
 * unknown keys — quietly undoing the loose-receipt guarantee this union exists
 * for. Measured on the installed zod (4.4.3): `.partial()` alone rejects an
 * unknown key, `.strip().partial()` accepts it. The "strips an unknown key on
 * receipt" case in messages.test.ts is what keeps that from regressing.
 */
export const ChoreStatusFrame = z.union([
  ChoreClaimedFrame.strip().partial({ claimExpiresAt: true }),
  ChoreAwaitingApprovalFrame.strip(),
  ChoreApprovedFrame.strip().partial({ approved_by: true }),
  ChoreRejectedFrame.strip(),
  ChoreExpiredFrame.strip(),
  ChoreReassignedFrame.strip(),
  ChoreRestoredFrame.strip(),
  ChoreCancelledFrame.strip(),
]);

/**
 * The exported TYPE is inferred from the STRICT union, deliberately: it is the
 * construction-side shape, where every field is required.
 *
 * Do not "simplify" this to `z.infer<typeof ChoreStatusFrame>` — the two are
 * NOT interchangeable, despite an earlier comment here claiming they differed
 * only in unknown-key policy. The loose union above applies
 * `.partial({claimExpiresAt:true})` and `.partial({approved_by:true})`, so its
 * inferred type has two OPTIONAL fields. Since issue #93 that distinction is
 * load-bearing rather than academic: WS_FRAMES maps this verb to
 * `ChoreStatusFrameOut`, so this is the type `hub.broadcast`'s `data`
 * parameter resolves to, and widening it would let a frame missing
 * `claimExpiresAt` compile at every call site.
 */
export type ChoreStatusFrame = z.infer<typeof ChoreStatusFrameOut>;

/**
 * The typed constructor every server broadcast on this verb goes through.
 * The parameter type catches a misspelling at COMPILE time; the strict parse
 * catches anything that reached runtime anyway (a cast, a spread, a widened
 * `unknown`). Both layers matter — since #93 the hub's own parameter is the
 * first of them (constraint 6), and this parse is the one that survives a
 * cast past it.
 */
export function choreStatusFrame(f: ChoreStatusFrame): ChoreStatusFrame {
  return ChoreStatusFrameOut.parse(f);
}

// ── Canonical fixtures ──────────────────────────────────────────────────────
// Exported so apps/api, apps/web and this package's own suite all assert
// against the SAME object rather than against literals each of them wrote.
// That three-way lock is the actual fix for #79 / #83 / #86.
//
// The ids are real 36-char UUIDv7s on purpose: the INV-WS-1 byte guard that
// consumes these fixtures has to measure what production actually sends, not
// a frame ~80 bytes smaller built from 'ci_1' / 'm_kid'.

export const CHORE_FRAME_INSTANCE_ID = '0199f3a2-7c4d-7e1b-9a55-3f2b8c6d1e04';
export const CHORE_FRAME_CHORE_ID = '0199f3a2-7c4d-7e1b-9a55-3f2b8c6d1e05';
export const CHORE_FRAME_KID_ID = '0199f3a2-7c4d-7e1b-9a55-3f2b8c6d1e06';
export const CHORE_FRAME_ADMIN_ID = '0199f3a2-7c4d-7e1b-9a55-3f2b8c6d1e07';
// Not exported: unlike the four ids, no suite needs it by name — it is
// reachable as CHORE_STATUS_FRAMES.claimed.claimExpiresAt.
const CHORE_FRAME_CLAIM_EXPIRES_AT = '2026-04-15T10:00:00.000Z';

export const CHORE_STATUS_FRAMES = {
  claimed: {
    id: CHORE_FRAME_INSTANCE_ID,
    status: 'claimed',
    claimed_by: CHORE_FRAME_KID_ID,
    claimExpiresAt: CHORE_FRAME_CLAIM_EXPIRES_AT,
  },
  awaiting_approval: {
    id: CHORE_FRAME_INSTANCE_ID,
    status: 'awaiting_approval',
    claimed_by: CHORE_FRAME_KID_ID,
  },
  approved: {
    id: CHORE_FRAME_INSTANCE_ID,
    status: 'completed',
    approved_by: CHORE_FRAME_ADMIN_ID,
    claimed_by: CHORE_FRAME_KID_ID,
    points: 12,
    prevBalance: 1250,
  },
  rejected: {
    id: CHORE_FRAME_INSTANCE_ID,
    status: 'available',
    cause: 'rejected',
  },
  expired: {
    id: CHORE_FRAME_INSTANCE_ID,
    status: 'available',
    cause: 'expired',
  },
  reassigned: {
    id: CHORE_FRAME_INSTANCE_ID,
    status: 'available',
    cause: 'reassigned',
  },
  restored: {
    id: CHORE_FRAME_INSTANCE_ID,
    status: 'available',
    cause: 'restored',
  },
  cancelled: {
    id: CHORE_FRAME_INSTANCE_ID,
    status: 'cancelled',
    cause: 'cancelled',
  },
} as const satisfies Record<string, ChoreStatusFrame>;
